/**
 * 堡垒锻造成就追踪系统。
 *
 * 追踪玩家进度并与预定义的成就进行比较，通过可注入的存储适配器进行持久化。
 * 游戏核心负责成就条件；UI/facade 驱动检查周期（例如每个事件或周期性检查）。
 *
 * 此模块是纯函数——无 GameEngine 依赖，无浏览器 API 依赖（CLAUDE.md §5）。
 * 通过 `StorageAdapter` 注入存储。
 */

import { gameContent } from "@bastion/data";
import type { AchievementCondition } from "@bastion/data";
import type { StorageAdapter, TimeSource } from "./repos";
import { BrowserStorageAdapter, SystemTimeSource } from "./repos";

/**
 * 检查时捕获的条件快照。不同类型的成就读取不同的字段——见下方的 CheckFn。
 */
export interface AchievementContext {
  /** 当前会话中击杀的敌人数 */
  enemiesKilled: number;
  /** 当前波次编号（从 1 开始；任何波次开始前为 0） */
  currentWave: number;
  /** 整个游戏中建造的塔数（非单波） */
  towersBuiltTotal: number;
  /** 本会话累计获得的金币（波次完成时的 rewardGold） */
  goldEarned: number;
  /** 玩家是否已完成最后一波 */
  finalWaveCompleted: boolean;
  /** 本轮游戏中到达基地的敌人累计损失的生命的总数 */
  livesLostThisGame: number;
  /** 第 1 波完成时的模拟时间（秒）（若尚未完成则为 0） */
  wave1CompleteTime: number;
  /** 检查时的总模拟时间（秒） */
  elapsedTime: number;
}

export interface Achievement {
  /** 用作存储键后缀的唯一 id */
  id: string;
  /** 成就解锁必须满足的条件 */
  condition: (ctx: AchievementContext) => boolean;
  /** 解锁时奖励的金币数（可为 0） */
  rewardGold: number;
}

export interface AchievementProgress {
  id: string;
  unlocked: boolean;
  /** 解锁时的 ISO-8601 时间戳（毫秒）；未解锁时为 0 */
  unlockedAt: number;
  /** 简要总结如何获得的不透明字符串（可选） */
  note?: string;
}

// ---------------------------------------------------------------------------
// 预定义成就定义
// ---------------------------------------------------------------------------

const DEFINED_ACHIEVEMENTS: Achievement[] = (gameContent.achievements ?? []).map((definition) => ({
  id: definition.id,
  condition: (ctx: AchievementContext) => evaluateCondition(definition.condition, ctx),
  rewardGold: definition.rewardGold,
}));

function evaluateCondition(condition: AchievementCondition, ctx: AchievementContext): boolean {
  const storage = BrowserStorageAdapter;
  if ("kind" in condition) {
    if (condition.kind === "speedDemon") {
      return (
        ctx.wave1CompleteTime > 0 && ctx.wave1CompleteTime < condition.value && ctx.currentWave >= 1
      );
    }
    if (condition.kind === "noDamage") return ctx.currentWave > 0 && ctx.livesLostThisGame === 0;
    if (condition.kind === "collector") {
      const progress = readProgress(storage);
      return DEFINED_ACHIEVEMENTS.filter((achievement) => achievement.id !== "collector").every(
        (achievement) => progress.some((entry) => entry.id === achievement.id && entry.unlocked),
      );
    }
    return false;
  }
  const value = ctx[condition.metric];
  if (condition.operator === ">=") return value >= condition.value;
  if (condition.operator === ">") return value > condition.value;
  if (condition.operator === "<") return value < condition.value;
  return value === condition.value;
}

/** 规范列表——启动后不允许修改。 */
export const ACHIEVEMENTS: ReadonlyArray<Achievement> = DEFINED_ACHIEVEMENTS;

// ---------------------------------------------------------------------------
// 内部辅助函数
// ---------------------------------------------------------------------------

const STORAGE_KEY = "bastion-achievements";

type RawProgress = Array<{ id: string; unlocked: boolean; unlockedAt: number; note?: string }>;

function readProgress(storage: StorageAdapter): Array<AchievementProgress> {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RawProgress;
    if (!Array.isArray(parsed)) return [];
    return parsed.map((p) => ({
      id: p.id,
      unlocked: Boolean(p.unlocked),
      unlockedAt: Number(p.unlockedAt) || 0,
      note: p.note,
    }));
  } catch {
    return [];
  }
}

function writeProgress(progress: Array<AchievementProgress>, storage: StorageAdapter): void {
  storage.setItem(STORAGE_KEY, JSON.stringify(progress));
}

function findProgress(
  progress: Array<AchievementProgress>,
  id: string,
): AchievementProgress | undefined {
  return progress.find((p) => p.id === id);
}

// ---------------------------------------------------------------------------
// 公共 API
// ---------------------------------------------------------------------------

/**
 * 根据提供的上下文检查所有成就，并解锁任何新成就。
 * 返回刚刚解锁的成就 id 列表（若无变化则返回空数组）。
 *
 * 此函数有意由 facade/UI 层在合适的时机调用（例如 enemy_killed 事件后、
 * 波次完成后等）。引擎本身不调用它，以保持核心逻辑与持久化之间的硬边界。
 */
export function checkAchievements(
  ctx: AchievementContext,
  _towersBuiltThisSession: number = 0,
  storage: StorageAdapter = BrowserStorageAdapter,
  timeSource: TimeSource = SystemTimeSource,
): string[] {
  const progress = readProgress(storage);
  const newlyUnlocked: string[] = [];

  for (const ach of ACHIEVEMENTS) {
    const existing = findProgress(progress, ach.id);
    if (existing?.unlocked) continue;
    if (!ach.condition(ctx)) continue;

    progress.push({
      id: ach.id,
      unlocked: true,
      unlockedAt: timeSource.now(),
      note: `Reward: ${ach.rewardGold} gold`,
    });
    newlyUnlocked.push(ach.id);
  }

  writeProgress(progress, storage);
  return newlyUnlocked;
}

/**
 * 返回所有成就及其当前进度。
 */
export function getAchievementProgress(
  storage: StorageAdapter = BrowserStorageAdapter,
): Array<AchievementProgress> {
  return readProgress(storage);
}

/**
 * 仅返回已解锁的成就。
 */
export function getUnlockedAchievements(
  storage: StorageAdapter = BrowserStorageAdapter,
): Array<AchievementProgress> {
  return readProgress(storage).filter((p) => p.unlocked);
}

/**
 * 强制解锁成就（适用于测试/GM 命令）。
 */
export function unlockAchievement(
  id: string,
  storage: StorageAdapter = BrowserStorageAdapter,
  timeSource: TimeSource = SystemTimeSource,
): AchievementProgress | null {
  const ach = ACHIEVEMENTS.find((a) => a.id === id);
  if (!ach) return null;

  const progress = readProgress(storage);
  const existing = findProgress(progress, id);
  if (existing?.unlocked) return existing;

  const entry: AchievementProgress = {
    id: ach.id,
    unlocked: true,
    unlockedAt: timeSource.now(),
    note: "手动解锁",
  };
  progress.push(entry);
  writeProgress(progress, storage);
  return entry;
}

/**
 * 将所有成就重置为未解锁状态。
 */
export function resetAchievements(storage: StorageAdapter = BrowserStorageAdapter): void {
  writeProgress([], storage);
}

/**
 * 获取指定成就 id 的金币奖励（如果已定义）。
 */
export function getAchievementReward(id: string): number {
  return ACHIEVEMENTS.find((a) => a.id === id)?.rewardGold ?? 0;
}
